import {
  ChainPublicClientService,
  isProdProtocolKey,
  makeUsd,
  MarketsByProtocol,
  ProtocolKey,
  PublicClientService,
} from "@silo/types";
import { Array, BigInt, Effect, pipe } from "effect";
import { MarketsStrategy } from "./types";
import { match, P } from "ts-pattern";
import { getPrimaryProtocolMarkets } from "./get-primary-protocol-markets";
import { getFallbackProtocolMarkets } from "./get-fallback-protocol-markets";
import { getConfigSuppressedTvlMarkets } from "@silo/config";

export type GetProtocolMarkets = ReturnType<typeof getProtocolMarkets>;
export type GetProtocolMarketsSuccess =
  Effect.Effect.Success<GetProtocolMarkets>;
export type GetProtocolMarketsError = Effect.Effect.Error<GetProtocolMarkets>;
export type GetProtocolMarketsContext =
  Effect.Effect.Context<GetProtocolMarkets>;

export function getProtocolMarkets(
  protocolKey: ProtocolKey,
  marketsStrategy: MarketsStrategy = "graph"
) {
  return Effect.gen(function* () {
    const { getPublicClient } = yield* PublicClientService;
    const publicClient = getPublicClient(protocolKey);

    const displayMarketArray = yield* pipe(
      match({
        protocolKey,
        marketsStrategy,
      })
        .with(
          { protocolKey: P.when(isProdProtocolKey), marketsStrategy: "graph" },
          ({ protocolKey }) =>
            Effect.firstSuccessOf([
              getPrimaryProtocolMarkets(protocolKey),
              getFallbackProtocolMarkets(protocolKey),
            ])
        )
        .with(
          { marketsStrategy: P.union("kv", "static") },
          ({ marketsStrategy }) =>
            getFallbackProtocolMarkets(protocolKey, marketsStrategy)
        )
        .otherwise(() => getFallbackProtocolMarkets(protocolKey)),
      Effect.provideService(ChainPublicClientService, { publicClient })
    );

    const suppressedTvlMarkets = getConfigSuppressedTvlMarkets(protocolKey);

    const tvlUsd = pipe(
      displayMarketArray,
      Array.flatMap((market) =>
        suppressedTvlMarkets.has(market.marketAddress) ? [] : [market.tvlUsd]
      ),
      BigInt.sumAll,
      makeUsd
    );

    const data: MarketsByProtocol[number] = {
      protocolKey,
      markets: displayMarketArray,
      tvlUsd,
    };

    return data;
  });
}
